import { ChangeDetectorRef, Component, ComponentFactoryResolver, ElementRef, EventEmitter, NgZone, OnDestroy, OnInit, QueryList, Type, ViewChild, ViewChildren } from '@angular/core';
import { Subscription } from 'rxjs';
import { NicaChatAiService } from '../../services/nica-chat-ai.service';
import { StoryChat, StoryChatMetadata } from './ai-interface';
import { DynamicHostDirective } from '../../directive/dynamicHost.directive';
import { DynamicContentAiComponent } from './dynamic-content-ai/dynamic-content-ai.component';

@Component({
  selector: 'app-new-nica-ai',
  templateUrl: './new-nica-ai.component.html',
  styleUrls: ['./new-nica-ai.component.css']
})
export class NewNicaAiComponent implements OnInit, OnDestroy {
  @ViewChild('storyChatContent', { static: false }) storyChatContent!: ElementRef<HTMLDivElement>;
  @ViewChildren(DynamicHostDirective) dynamicHosts!: QueryList<DynamicHostDirective>;
  protected isPopoverVisible = false;
  protected readonly nameAgent: string = 'Bot'
  protected input: string = ''
  private subConversazione: Subscription;
  private subError: Subscription;
  private subLoading: Subscription;
  private subClearing: Subscription;
  private pendingCreate: Array<StoryChatMetadata> = [];
  private hostIndex = new Map<string, DynamicHostDirective>();
  private createsDone = new Set<string>();            // per non ricreare due volte lo stesso hostId
  private dynamicHostsSubscribed = false;             // per evitare subscribe multiple
  conversazione: StoryChat[] = [];
  loading: boolean = false
  error: boolean = false
  clearing: boolean = false
  constructor(
    private readonly NicaChatAiService: NicaChatAiService,
    private readonly zone: NgZone,
    private readonly cdr: ChangeDetectorRef,
    private readonly el: ElementRef,
    private readonly componentFactoryResolver: ComponentFactoryResolver) {
  }

  ngOnInit() {
    this.subError = this.NicaChatAiService.error$.subscribe(val => {
      this.error = val
      this.cdr.detectChanges()
    })
    this.subLoading = this.NicaChatAiService.loading$.subscribe(val => {
      this.loading = val
      this.cdr.detectChanges()
    })
    this.subClearing = this.NicaChatAiService.clearing$.subscribe(val => {
      this.clearing = val
      this.cdr.detectChanges()
    })
    this.NicaChatAiService.retriveConversazione().subscribe(() => {
      this.cdr.detectChanges()
      setTimeout(() => this.scrollToBottom(), 0);
    })
    this.subConversazione = this.NicaChatAiService.conversazione$.subscribe(msgs => {
      this.conversazione = msgs;
      this.repaintDynamic()
      this.cdr.detectChanges()
      setTimeout(() => this.scrollToBottom(), 0);
    });
    if (!this.dynamicHostsSubscribed) {
      this.dynamicHostsSubscribed = true;
      this.dynamicHosts.changes.subscribe(() => {
        this.rebuildHostIndex();
        this.flushCreates(); // appena compaiono host nuovi, prova a drenare la coda
      });
    }
    // primo build se già presenti
    queueMicrotask(() => this.rebuildHostIndex());
  }

  ngOnDestroy() {
    if (this.subConversazione) this.subConversazione.unsubscribe();
    if (this.subError) this.subError.unsubscribe();
    if (this.subLoading) this.subLoading.unsubscribe();
    if (this.subClearing) this.subClearing.unsubscribe();
  }

  protected togglePopover(e: any) {
    e.component.option('elementAttr', { id: 'popover-btn' });
    this.isPopoverVisible = !this.isPopoverVisible;
    this.hostIndex.clear();
    this.createsDone.clear();
    this.cdr.detectChanges();
    this.repaintDynamic();
    setTimeout(() => this.scrollToBottom(), 0);
  }

  protected onKeyDown(e: any) {
    if (this.loading) return;
    const ev = e.event as KeyboardEvent;
    if (!ev) return;
    if (ev.key !== 'Enter' || ev.shiftKey) return;

    ev.preventDefault();
    ev.stopPropagation();

    const comp = e.component;
    if (!comp) return
    const raw = comp.option('text') || comp.option('value') || '';
    const text = String(raw).trim();
    if (!text) return;

    this.input = text
    this.sendMessage()
    this.zone.runOutsideAngular(() => {
      queueMicrotask(() => {
        comp.reset();
        comp._refresh()
        this.zone.run(() => {
          this.input = '';
          this.cdr.detectChanges();
        });
      });
    });
  }

  protected svuotaChat() {
    this.NicaChatAiService.clear()
    this.hostIndex.clear();
    this.createsDone.clear(); // forza la ricreazione
    this.cdr.detectChanges();
  }

  protected scrollToBottom() {
    if (!this.storyChatContent) return
    const el = this.storyChatContent.nativeElement;
    if (!el) return;
    el.scrollTo({
      top: el.scrollHeight,
      behavior: 'smooth'
    });
  }

  protected async sendMessage() {
    const text = this.input.trim();
    if (text === '') return;
    this.NicaChatAiService.addMessage([{ content: text, role: 'user', date: new Date() }]);
    this.input = '';
    setTimeout(() => this.scrollToBottom(), 0);
  }

  private rebuildHostIndex() {
    this.hostIndex.clear();
    if (!this.dynamicHosts) return;
    for (const d of this.dynamicHosts.toArray()) {
      if (d.hostId) this.hostIndex.set(d.hostId, d);
    }
  }

  private repaintDynamic() {
    for (let el = 0; el < this.conversazione.length; el++) {
      if (!this.conversazione[el].content.includes('```json') || !this.conversazione[el].metadata) continue;
      const { hostId, data, isProcessed } = this.conversazione[el].metadata;
      if (!this.createsDone.has(hostId)) {
        this.scheduleCreate({ hostId, data, isProcessed: el !== this.conversazione.length - 1 });
      }
    }
  }


  private scheduleCreate({ data, hostId, isProcessed }: StoryChatMetadata) {
    if (this.createsDone.has(hostId)) return;
    this.pendingCreate.push({ hostId, data, isProcessed });
    this.cdr.detectChanges();
    this.flushCreates();
  }


  private flushCreates() {
    if (!this.pendingCreate.length) return;
    const queue = [...this.pendingCreate];
    this.pendingCreate.length = 0;
    for (const item of queue) {
      if (this.createsDone.has(item.hostId)) continue;
      const host = this.hostIndex.get(item.hostId) || (this.dynamicHosts && this.dynamicHosts.find(h => h.hostId === item.hostId));
      if (!host) {
        this.pendingCreate.push(item);
        continue;
      }
      const vcr = host.viewContainerRef;
      vcr.clear();
      const factory = this.componentFactoryResolver.resolveComponentFactory(DynamicContentAiComponent);
      const componentRef = vcr.createComponent(factory);
      Object.assign(componentRef.instance, { data: item.data, isProcessed: item.isProcessed })
      this.createsDone.add(item.hostId);
      Object.keys(componentRef.instance).forEach(property => {
        const propertyValue = (componentRef.instance as any)[property];
        if (propertyValue instanceof EventEmitter) {
          propertyValue.subscribe((eventData: any) => {
            console.log(eventData)
            if (eventData.name === 'selection') {
              if (eventData.data.length > 0) {
                const msg = { role: 'user', date: new Date(), content: (eventData.data as string[]).join(', ') } as StoryChat
                this.NicaChatAiService.addMessage([{ ...msg }])
                setTimeout(() => this.scrollToBottom(), 0);
              }
            }
          });
        }
      });
    }
    setTimeout(() => this.scrollToBottom(), 0);
  }
}
